import { DateStrategyAssign, FixedDurationDates, SpecificStartDates } from "app/shared/model/EditPricelist";
import * as React from "react";
import { connect } from "react-redux";
import { Dispatch } from "redux";
import { getPricingYears, getSelectedPricingYear } from "../store/selectors";
import { DateStrategy, IsoDayOfWeek } from "app/shared/model/EditProductOffering";
import { Button, Checkbox, Row, Col, Table, DatePicker } from "antd";
import { ListLayout, ListLayoutItem } from "app/shared/ui/ListLayout";
import dayjs, { Dayjs } from "dayjs";
import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import DateHelper, { ManipulationTypes, CompareTypes } from "app/helpers/dateHelper";
import { PricingYearId } from "app/shared/model";
import "app/shared/components/Table/Table.css";
import Notification from "app/shared/components/Notification/Notification";
import { CurrentUser } from "app/shared/model/CurrentUser";
import { getCurrentUser } from "app/shared/currentUser";
import { toIso } from '../domain/date/iso';
import { normalizeStrategy } from '../domain/dateStrategy/normalize';
import {cloneDeep} from "app/pages/PricelistNew/components/utils/cloneDeep";
import {
    maxIsoDate,
    minIsoDate,
    shiftPickerDateToEarliestAllowed,
} from "app/pages/PricelistNew/domain/pricingYear/window";
import {
    isWorkingWeeksUnitType,
    isWeekendDate,
    WORKING_WEEKS_WEEKDAY_START_MESSAGE,
} from "../domain/dateStrategy/workingWeeksStartDate";
type NotificationColor =
    | "success"
    | "error"
    | "warning"
    | "red"
    | "orange"
    | "green"
    | "blue"
    | "info";

type UiLabel = {
    content: string;
    color?: NotificationColor;
    active: boolean;
};

interface DateStrategyContentProps {
    dateStrategyToUpdateCreate?: DateStrategyAssign;
    allowedStartDate: string;
    allowedEndDate: string;
    createNew: boolean;
    isAddOfferingFlow?: boolean;
    productUnitTypeId?: number;
    productUnitTypeName?: string;
    onPendingChanges: (changes: boolean) => void;
    onDateStrategyUpdate?: (dateStrategy: DateStrategyAssign) => void;
}

interface StateProps {
    pricingYears: PricingYearId[];
    selectedPricingYear?: number;
    user: CurrentUser;
}

interface DispatchProps {}

type InternalProps = StateProps & DispatchProps & DateStrategyContentProps;

const DateStrategyContentView = (props: InternalProps) => <>{DateStrategyContent(props)}</>;

const hasPendingStrategyData = (strategy?: DateStrategyAssign): boolean => {
    if (!strategy) return false;

    const t = strategy.strategyTypeId;

    if (DateStrategy[t] === "Offering Dates") return true;
    if (DateStrategy[t] === "Recurrence") {
        return Boolean(strategy.recurranceStartDays?.length);
    }
    if (DateStrategy[t] === "Fixed Duration") {
        return Boolean(strategy.fixedDurationDates?.filter((d) => !d.isDeleted).length);
    }
    if (DateStrategy[t] === "Specific Start Dates") {
        return Boolean(strategy.specificStartDates?.filter((d) => !d.isDeleted).length);
    }

    return false;
};

const clampStrategyDetailsToWindow = (
    strategy: DateStrategyAssign,
    minDate: string,
    maxDate: string,
    createNew: boolean
): DateStrategyAssign => {
    const next = normalizeStrategy(cloneDeep(strategy));
    const isUnsaved = (d: { isNew?: boolean }) => createNew || d.isNew === true;

    next.specificStartDates = (next.specificStartDates ?? []).filter((d) => {
        if (d.isDeleted || !isUnsaved(d)) return true;

        const iso = toIso(d.date);
        return Boolean(iso) && iso >= minDate && iso <= maxDate;
    });

    next.fixedDurationDates = (next.fixedDurationDates ?? []).flatMap((d) => {
        if (d.isDeleted || !isUnsaved(d)) return [d];

        const start = maxIsoDate(toIso(d.startDate) || minDate, minDate);
        const end = minIsoDate(toIso(d.endDate) || maxDate, maxDate);

        if (DateHelper.CompareDates(end, start, CompareTypes.SmallerThan)) {
            return [];
        }

        return [
            {
                ...d,
                startDate: start as any,
                endDate: end as any,
            },
        ];
    });

    const liveStartDates = (next.specificStartDates ?? [])
        .filter((d) => !d.isDeleted)
        .map((d) => toIso(d.date))
        .filter(Boolean);

    if (liveStartDates.length > 0) {
        next.lastStartDate = liveStartDates.reduce((a, b) => (a > b ? a : b)) as any;
    }

    return next;
};

const resolveAppliesFromPickerDate = (
    strategy: DateStrategyAssign | undefined,
    createNew: boolean
): string => {
    if (!createNew || !strategy) return "";

    const strategyName = DateStrategy[strategy.strategyTypeId];
    if (strategyName !== "Fixed Duration" && strategyName !== "Specific Start Dates") {
        return "";
    }

    const fromIso = toIso(strategy.fromDate);
    return fromIso && !isNaN(Date.parse(fromIso)) ? fromIso : "";
};

function DateStrategyContent(props: InternalProps) {
    if (!props.dateStrategyToUpdateCreate) return <>loading...</>;

    const createClampedStrategy = React.useCallback(
        (source: DateStrategyAssign): DateStrategyAssign => {
            const normalized = normalizeStrategy(cloneDeep(source));

            const strategyFromDate = toIso(normalized.fromDate) || props.allowedStartDate;
            const strategyToDate = toIso(normalized.toDate) || props.allowedEndDate;

            const effectiveMinDate = maxIsoDate(strategyFromDate, props.allowedStartDate);
            const effectiveMaxDate = minIsoDate(strategyToDate, props.allowedEndDate);

            return clampStrategyDetailsToWindow(
                normalized,
                effectiveMinDate,
                effectiveMaxDate,
                props.createNew
            );
        },
        [props.allowedStartDate, props.allowedEndDate, props.createNew]
    );

    const [dateStrategy, setDateStrategy] = React.useState<DateStrategyAssign>(() =>
        createClampedStrategy(props.dateStrategyToUpdateCreate!)
    );

    React.useEffect(() => {
        if (!props.dateStrategyToUpdateCreate) return;

        const normalized = normalizeStrategy(cloneDeep(props.dateStrategyToUpdateCreate));
        const next = createClampedStrategy(props.dateStrategyToUpdateCreate);

        setDateStrategy(next);
        props.onPendingChanges(hasPendingStrategyData(next));

        if (JSON.stringify(next) !== JSON.stringify(normalized)) {
            props.onDateStrategyUpdate?.(next);
        }
    }, [
        props.dateStrategyToUpdateCreate?.strategyTypeId,
        props.dateStrategyToUpdateCreate?.fromDate,
        props.dateStrategyToUpdateCreate?.toDate,
        props.dateStrategyToUpdateCreate?.recurranceStartDays,
        props.dateStrategyToUpdateCreate?.specificStartDates,
        props.dateStrategyToUpdateCreate?.fixedDurationDates,
        createClampedStrategy,
        props.onPendingChanges,
        props.onDateStrategyUpdate,
    ]);

    const [selectedDateFrom, setSelectedDateFrom] = React.useState<string>(() =>
        resolveAppliesFromPickerDate(props.dateStrategyToUpdateCreate, props.createNew)
    );
    const [selectedDateTo, setSelectedDateTo] = React.useState<string>("");
    const [label, setLabel] = React.useState<UiLabel | undefined>(undefined);
    const [labelCounter, setLabelCounter] = React.useState<number>(0);
    const [fieldError, setFieldError] = React.useState<'from'|'to'|'both'|undefined>(undefined);

    React.useEffect(() => {
        if (!props.createNew) return;

        const fromIso = resolveAppliesFromPickerDate(dateStrategy, true);
        if (!fromIso) return;

        setSelectedDateFrom((prev) => (prev ? prev : fromIso));
    }, [props.createNew, dateStrategy.fromDate, dateStrategy.strategyTypeId]);

    React.useEffect(() => {
        if (label && label.active) {
            setLabelCounter((c) => c + 1);
        }
    }, [label?.active]);

    React.useEffect(() => {
        const isWorkingWeeks = isWorkingWeeksUnitType(
            props.productUnitTypeId,
            props.productUnitTypeName
        );
        if (!isWorkingWeeks) {
            return;
        }

        const hasSaturdayOrSunday = (dateStrategy.recurranceStartDays ?? []).some(
            (d) => d === IsoDayOfWeek.Saturday || d === IsoDayOfWeek.Sunday
        );
        if (!hasSaturdayOrSunday) return;

        const next = cloneDeep(dateStrategy);
        next.recurranceStartDays = (next.recurranceStartDays ?? []).filter(
            (d) => d !== IsoDayOfWeek.Saturday && d !== IsoDayOfWeek.Sunday
        );
        setDateStrategy(next);
        props.onPendingChanges(hasPendingStrategyData(next));
        props.onDateStrategyUpdate?.(next);
    }, [dateStrategy.recurranceStartDays, props.productUnitTypeId, props.productUnitTypeName]);

    const NO_OF_ROWS = 5;

    const strategyFromDate = toIso(dateStrategy.fromDate) || props.allowedStartDate;
    const strategyToDate = toIso(dateStrategy.toDate) || props.allowedEndDate;

    const effectiveMinDate = maxIsoDate(strategyFromDate, props.allowedStartDate);
    const effectiveMaxDate = minIsoDate(strategyToDate, props.allowedEndDate);

    const removeDates = (date: SpecificStartDates | FixedDurationDates) => {
        if (!date) return;

        const strategyName = DateStrategy[dateStrategy.strategyTypeId];

        if (strategyName === "Specific Start Dates") {
            const list = dateStrategy.specificStartDates ?? [];
            const idx = list.indexOf(date as SpecificStartDates);
            if (idx < 0) return;

            const next = cloneDeep(dateStrategy);
            if (props.createNew) {
                next.specificStartDates = list.filter((d) => d !== date);
            } else {
                next.specificStartDates = list.slice();
                next.specificStartDates[idx] = {
                    ...next.specificStartDates[idx],
                    isDeleted: true,
                };
            }

            setDateStrategy(next);
            props.onPendingChanges(hasPendingStrategyData(next));
            props.onDateStrategyUpdate?.(next);
            return;
        }

        if (strategyName === "Fixed Duration") {
            const list = dateStrategy.fixedDurationDates ?? [];
            const idx = list.indexOf(date as FixedDurationDates);
            if (idx < 0) return;

            const next = cloneDeep(dateStrategy);
            if (props.createNew) {
                next.fixedDurationDates = list.filter((d) => d !== date);
            } else {
                next.fixedDurationDates = list.slice();
                next.fixedDurationDates[idx] = {
                    ...next.fixedDurationDates[idx],
                    isDeleted: true,
                };
            }

            setDateStrategy(next);
            props.onPendingChanges(hasPendingStrategyData(next));
            props.onDateStrategyUpdate?.(next);
        }
    };

    const getData = (data: SpecificStartDates[], yIndex: number, xIndex: number, noOfRows: number, button: boolean = false) => {
        if (button) yIndex--;
        const dataIndex = ((yIndex === 0) ? xIndex : ((((yIndex / 2) * noOfRows)) + xIndex));
        return (dataIndex > data.length - 1) ? '' : (button)
            ? <Button icon={<DeleteOutlined style={{ color: 'red' }} />} size='small' onClick={() => removeDates(data[dataIndex])} />
            : DateHelper.ToDateString(data[dataIndex].date);
    }

    const getDataFixedDuration = (
        data: FixedDurationDates[],
        yIndex: number,
        xIndex: number,
        noOfRows: number,
        button = false
    ) => {
        if (button) yIndex--;
        const dataIndex = yIndex === 0 ? xIndex : (yIndex / 2) * noOfRows + xIndex;

        if (dataIndex > data.length - 1) return "";
        if (button) {
            return (
                <Button
                    icon={<DeleteOutlined style={{ color: "red" }} />}
                    size="small"
                    onClick={() => removeDates(data[dataIndex])}
                />
            );
        }
        return `${DateHelper.ToDateString(data[dataIndex].startDate)} - ${DateHelper.ToDateString(
            data[dataIndex].endDate
        )}`;
    };

    const addNewDates = () => {
        if (!selectedDateFrom || isNaN(Date.parse(selectedDateFrom))) {
            setLabel({ content: `'Date From' cannot be empty`, color: "orange", active: true });
            return;
        }

        if (selectedDateFrom < effectiveMinDate || selectedDateFrom > effectiveMaxDate) {
            setLabel({
                content: `'Date From' should be between '${effectiveMinDate}' and '${effectiveMaxDate}'`,
                color: "orange",
                active: true,
            });
            return;
        }

        const strategyName = DateStrategy[dateStrategy.strategyTypeId];
        let success = true;

        const next = cloneDeep(dateStrategy);

        if (strategyName === "Specific Start Dates") {
            if (
                isWorkingWeeksUnitType(props.productUnitTypeId, props.productUnitTypeName) &&
                isWeekendDate(selectedDateFrom)
            ) {
                success = false;
                setFieldError("from");
                setLabel({
                    content: WORKING_WEEKS_WEEKDAY_START_MESSAGE,
                    color: "red",
                    active: true,
                });
            } else {
            next.specificStartDates = next.specificStartDates ?? [];

            const specificStartDate: SpecificStartDates = {
                date: selectedDateFrom,
                isNew: true,
                isDeleted: false,
            };

            const exists = next.specificStartDates.some(
                (x) => !x.isDeleted && toIso(x.date) === toIso(specificStartDate.date)
            );

            if (!exists) {
                next.specificStartDates.push(specificStartDate);
                setFieldError(undefined);
            } else {
                success = false;
                setFieldError('from');
                setLabel({
                    content: `'${DateHelper.ToDateString(specificStartDate.date)}' date already exists`,
                    color: "red",
                    active: true,
                });
            }

            setSelectedDateFrom("");
            }
        } else if (strategyName === "Fixed Duration") {
            if (!selectedDateTo || isNaN(Date.parse(selectedDateTo))) {
                success = false;
                setFieldError('to');
                setLabel({ content: `'Date To' cannot be empty`, color: "orange", active: true });
            } else if (selectedDateTo <= selectedDateFrom || selectedDateTo > effectiveMaxDate) {
                success = false;
                setFieldError('to');
                setLabel({
                    content: `'Date To' should be between '${selectedDateFrom}' and '${effectiveMaxDate}'`,
                    color: "orange",
                    active: true,
                });
            } else {
                next.fixedDurationDates = next.fixedDurationDates ?? [];

                const exists = next.fixedDurationDates.some(
                    (x) =>
                        !x.isDeleted &&
                        toIso(x.startDate) === toIso(selectedDateFrom) &&
                        toIso(x.endDate) === toIso(selectedDateTo)
                );

                if (!exists) {
                    next.fixedDurationDates.push({
                        startDate: selectedDateFrom,
                        endDate: selectedDateTo,
                        isNew: true,
                        isDeleted: false,
                    });

                    setSelectedDateFrom(resolveAppliesFromPickerDate(next, props.createNew));
                    setSelectedDateTo("");
                    setFieldError(undefined);
                } else {
                    success = false;
                    setFieldError("both");
                    setLabel({
                        content: `'${DateHelper.ToDateString(selectedDateFrom)} - ${DateHelper.ToDateString(
                            selectedDateTo
                        )}' date range already exists`,
                        color: "red",
                        active: true,
                    });
                }
            }
        }

        if (!success) return;

        const clamped = clampStrategyDetailsToWindow(
            next,
            effectiveMinDate,
            effectiveMaxDate,
            props.createNew
        );

        setDateStrategy(clamped);
        props.onPendingChanges(hasPendingStrategyData(clamped));
        setLabel({ content: `Date added successfully`, color: "green", active: true });
        props.onDateStrategyUpdate?.(clamped);
    };

    const updateReccurence = (dayValue: IsoDayOfWeek, checked: boolean) => {
        const strategyName = DateStrategy[dateStrategy.strategyTypeId];
        if (strategyName !== "Recurrence") return;

        const isWorkingWeeks = isWorkingWeeksUnitType(
            props.productUnitTypeId,
            props.productUnitTypeName
        );

        if (checked && isWorkingWeeks && (dayValue === IsoDayOfWeek.Saturday || dayValue === IsoDayOfWeek.Sunday)) {
            return;
        }

        const next = cloneDeep(dateStrategy);
        next.recurranceStartDays = next.recurranceStartDays ?? [];

        if (checked) {
            if (!next.recurranceStartDays.includes(dayValue)) {
                next.recurranceStartDays.push(dayValue);
            }
        } else {
            next.recurranceStartDays = next.recurranceStartDays.filter((r) => r !== dayValue);
        }

        setDateStrategy(next);
        props.onPendingChanges(hasPendingStrategyData(next));
        props.onDateStrategyUpdate?.(next);
    };

    const datePickerDisabledDate = (minIso: string, maxIso: string) => (date: Dayjs) =>
        date.isBefore(dayjs(minIso), "day") || date.isAfter(dayjs(maxIso), "day");

    const generateAddNewSection = () => {
        const showAddNew = dateStrategy.strategyTypeId !== 1;
        const showDateTo = dateStrategy.strategyTypeId === 2;

        if (!showAddNew) return null;

        const fromFieldStyle =
            fieldError === "from" || fieldError === "both"
                ? { width: 140, borderColor: "red", boxShadow: "0 0 0 2px rgba(255,0,0,0.08)" }
                : { width: 140 };

        const toFieldStyle =
            fieldError === "to" || fieldError === "both"
                ? { width: 140, borderColor: "red", boxShadow: "0 0 0 2px rgba(255,0,0,0.08)" }
                : { width: 140 };

        return (
            <Row gutter={8} className="add" style={{ marginBottom: 12 }}>
                <Col>
                    {showDateTo ? "Date From" : "Date"}
                    <DatePicker
                        format={DateHelper.DATE_PICKER_FORMATS}
                        placeholder="Select date"
                        value={selectedDateFrom ? dayjs(selectedDateFrom) : null}
                        disabledDate={datePickerDisabledDate(effectiveMinDate, effectiveMaxDate)}
                        onChange={(value) => {
                            if (!value) {
                                setSelectedDateFrom("");
                                return;
                            }
                            const nextFrom = shiftPickerDateToEarliestAllowed(
                                value,
                                effectiveMinDate,
                                effectiveMaxDate
                            );
                            setSelectedDateFrom(nextFrom);
                            setFieldError((prev) => (prev === "from" ? undefined : prev));

                            if (selectedDateTo && nextFrom && selectedDateTo < nextFrom) {
                                setSelectedDateTo(nextFrom);
                            }
                        }}
                        style={fromFieldStyle}
                    />
                </Col>

                {showDateTo && (
                    <Col>
                        Date To
                        <DatePicker
                            format={DateHelper.DATE_PICKER_FORMATS}
                            placeholder="Select date"
                            disabled={!selectedDateFrom}
                            value={selectedDateTo ? dayjs(selectedDateTo) : null}
                            disabledDate={datePickerDisabledDate(
                                selectedDateFrom || effectiveMinDate,
                                effectiveMaxDate
                            )}
                            onChange={(value) => {
                                if (!value) {
                                    setSelectedDateTo("");
                                    return;
                                }
                                const nextTo = shiftPickerDateToEarliestAllowed(
                                    value,
                                    selectedDateFrom || effectiveMinDate,
                                    effectiveMaxDate
                                );
                                setSelectedDateTo(nextTo);
                                setFieldError((prev) => (prev === "to" ? undefined : prev));
                            }}
                            style={toFieldStyle}
                        />
                    </Col>
                )}

                <Col flex="auto" style={{ textAlign: "right" }}>
                    {label ? (
                        <Notification
                            pointing="right"
                            dedupeKey={`date-added-${labelCounter}`}
                            {...label}
                            visible={label.active}
                            onHide={() =>
                                setLabel((prev) => (prev ? { ...prev, active: false } : undefined))
                            }
                            autoHide
                            autoHideSeconds={3}
                        />
                    ) : null}
                    <Button type="primary" icon={<PlusOutlined />} onClick={addNewDates}>
                        Add
                    </Button>
                </Col>
            </Row>
        );
    };

    const generateContentSection = () => {
        switch (DateStrategy[dateStrategy.strategyTypeId]) {
            case "Recurrence": {
                const dayNames = Object.keys(IsoDayOfWeek).filter(
                    (k) => isNaN(Number(k)) && k !== "None"
                );

                const isWorkingWeeks = isWorkingWeeksUnitType(
                    props.productUnitTypeId,
                    props.productUnitTypeName
                );

                return (
                    <ListLayout size="small">
                        {dayNames.map((name) => {
                            const dayValue = (IsoDayOfWeek as any)[name] as IsoDayOfWeek;
                            const isChecked = (dateStrategy.recurranceStartDays ?? []).includes(dayValue);
                            const isDisabled = isWorkingWeeks && (dayValue === IsoDayOfWeek.Saturday || dayValue === IsoDayOfWeek.Sunday);

                            return (
                                <ListLayoutItem key={name} style={{ padding: "10px 0" }}>
                                    <Checkbox
                                        checked={isChecked}
                                        disabled={isDisabled}
                                        onChange={(e) => updateReccurence(dayValue, e.target.checked)}
                                    >
                                        {name}
                                    </Checkbox>
                                </ListLayoutItem>
                            );
                        })}
                    </ListLayout>
                );
            }

            case "Fixed Duration": {
                let fdData =
                    dateStrategy.fixedDurationDates?.filter(
                        (d) =>
                            (DateHelper.CompareDates_Between(
                                d.startDate,
                                effectiveMinDate,
                                effectiveMaxDate,
                                true
                            ) || d.isNew) &&
                            !d.isDeleted
                    ) ?? [];

                fdData = fdData
                    .sort((a, b) =>
                        DateHelper.DateManipulation(b.startDate, a.startDate, ManipulationTypes.Subtract)
                    )
                    .reverse();

                const fdNoOfRows = Math.min(NO_OF_ROWS, fdData.length);
                const noOfColumns = Math.ceil(fdData.length / NO_OF_ROWS) * 2;

                const columns: any[] = [];
                for (let i = 0; i < noOfColumns; i++) {
                    columns.push(
                        i % 2 === 0
                            ? { title: "Dates", dataIndex: `date${i}`, key: `date${i}`, render: (text: any) => text }
                            : { title: "", dataIndex: `action${i}`, key: `action${i}`, render: (text: any) => text }
                    );
                }

                const dataSource: any[] = [];
                for (let x = 0; x < fdNoOfRows; x++) {
                    const row: any = { key: x };
                    for (let y = 0; y < noOfColumns; y++) {
                        row[y % 2 === 0 ? `date${y}` : `action${y}`] = getDataFixedDuration(
                            fdData,
                            y,
                            x,
                            fdNoOfRows,
                            y % 2 === 1
                        );
                    }
                    dataSource.push(row);
                }

                return (
                    <Table
                        columns={columns}
                        dataSource={dataSource}
                        pagination={false}
                        bordered
                        size="small"
                        style={{ margin: 15 }}
                    />
                );
            }

            case "Offering Dates":
                return <></>;

            case "Specific Start Dates": {
                let data =
                    dateStrategy.specificStartDates?.filter(
                        (d) =>
                            (DateHelper.CompareDates_Between(
                                d.date,
                                effectiveMinDate,
                                effectiveMaxDate,
                                true
                            ) || d.isNew) &&
                            !d.isDeleted
                    ) ?? [];

                data = data
                    .sort((a, b) => DateHelper.DateManipulation(b.date, a.date, ManipulationTypes.Subtract))
                    .reverse();

                const noOfRows = Math.min(NO_OF_ROWS, data.length);
                const noOfColumns = Math.ceil(data.length / NO_OF_ROWS) * 2;

                const columns: any[] = [];
                for (let i = 0; i < noOfColumns; i++) {
                    columns.push(
                        i % 2 === 0
                            ? { title: "Dates", dataIndex: `date${i}`, key: `date${i}`, render: (text: any) => text }
                            : { title: "", dataIndex: `action${i}`, key: `action${i}`, render: (text: any) => text }
                    );
                }

                const dataSource: any[] = [];
                for (let x = 0; x < noOfRows; x++) {
                    const row: any = { key: x };
                    for (let y = 0; y < noOfColumns; y++) {
                        row[y % 2 === 0 ? `date${y}` : `action${y}`] = getData(
                            data,
                            y,
                            x,
                            noOfRows,
                            y % 2 === 1
                        );
                    }
                    dataSource.push(row);
                }

                return (
                    <Table
                        columns={columns}
                        dataSource={dataSource}
                        pagination={false}
                        bordered
                        size="small"
                        style={{ margin: 15 }}
                    />
                );
            }

            default:
                return <>Unsupported date strategy type.</>;
        }
    };

    return (
        <div className="container">
            {generateAddNewSection()}
            <Row>
                <Col span={24} className="overflow-x">
                    {generateContentSection()}
                </Col>
            </Row>
        </div>
    );
}

const mapStateToProps = (state: any): StateProps => ({
    pricingYears: getPricingYears(state),
    selectedPricingYear: getSelectedPricingYear(state),
    user: getCurrentUser(state),
});

const mapDispatchToProps = (_dispatch: Dispatch<any>): DispatchProps => ({});

export default connect(mapStateToProps, mapDispatchToProps)(DateStrategyContentView);